import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, CheckCheck, Eye, MousePointerClick, AlertTriangle, Ban, OctagonAlert } from 'lucide-react';

type BadgeVariant = 'success' | 'destructive' | 'secondary' | 'info' | 'warning';

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: BadgeVariant; icon?: React.ComponentType<{ className?: string }> }
> = {
  sent:         { label: 'sent',         variant: 'secondary',   icon: Check },
  processed:    { label: 'sent',         variant: 'secondary',   icon: Check },
  deferred:     { label: 'deferred',     variant: 'warning' },
  delivered:    { label: 'delivered',    variant: 'info',        icon: CheckCheck },
  opened:       { label: 'opened',       variant: 'success',     icon: Eye },
  clicked:      { label: 'clicked',      variant: 'success',     icon: MousePointerClick },
  bounced:      { label: 'bounced',      variant: 'destructive', icon: AlertTriangle },
  dropped:      { label: 'dropped',      variant: 'destructive', icon: Ban },
  spam:         { label: 'spam',         variant: 'destructive', icon: OctagonAlert },
  unsubscribed: { label: 'unsubscribed', variant: 'destructive' },
  failed:       { label: 'failed',       variant: 'destructive', icon: AlertTriangle },
};

function formatTs(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function EmailDeliveryStatusBadge({ metadata }: { metadata?: Record<string, any> }) {
  if (!metadata?.status) return null;

  const status = String(metadata.status);
  const cfg = STATUS_CONFIG[status] ?? { label: status, variant: 'secondary' as BadgeVariant };
  const Icon = cfg.icon;

  const tooltipLines = [
    metadata.sent_at && `Sent: ${formatTs(metadata.sent_at)}`,
    metadata.delivered_at && `Delivered: ${formatTs(metadata.delivered_at)}`,
    metadata.opened_at && `Opened: ${formatTs(metadata.opened_at)}${metadata.open_count > 1 ? ` (${metadata.open_count}×)` : ''}`,
    metadata.clicked_at && `Clicked: ${formatTs(metadata.clicked_at)}${metadata.click_count > 1 ? ` (${metadata.click_count}×)` : ''}`,
    metadata.bounced_at && `Bounced: ${formatTs(metadata.bounced_at)}${metadata.bounce_reason ? ` — ${metadata.bounce_reason}` : ''}`,
    metadata.spam_at && `Spam: ${formatTs(metadata.spam_at)}`,
    metadata.deferred_at && `Deferred: ${formatTs(metadata.deferred_at)}`,
  ].filter(Boolean) as string[];

  const badge = (
    <Badge variant={cfg.variant as any} className="text-[10px] px-1.5 py-0 gap-0.5">
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {cfg.label}
    </Badge>
  );

  if (tooltipLines.length === 0) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          {tooltipLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
